import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Cookies } from 'react-cookie';
import { storageKeys } from 'src/helpers/mobileConstants';

interface MobileData {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

interface MobileContextType {
  mobileData: MobileData;
  setMobileData: (data: MobileData) => void;
  updateMobileData: (newData: MobileData) => void;
}

const MobileContext = createContext<MobileContextType | undefined>(undefined);

interface MobileProviderProps {
  children: ReactNode;
}

export const MobileProvider: React.FC<MobileProviderProps> = ({ children }) => {
  const [mobileData, setMobileData] = useState<MobileData | null>(null);
  const cookies = new Cookies();

  useEffect(() => {
    // Initialize mobile data from cookies when the component mounts
    const savedMobileData = cookies.get(storageKeys.MOBILE_DATA_KEY);
    if (savedMobileData) {
      try {
        const parsedData = JSON.parse(savedMobileData);
        setMobileData(parsedData);
      } catch (error) {
        console.error('Error parsing mobile data from cookies:', error);
      }
    }
  }, []);

  const updateMobileData = (newData: MobileData) => {
    setMobileData((prevData: MobileData) => {
      const updatedData = { ...prevData, ...newData };
      cookies.set(storageKeys.MOBILE_DATA_KEY, JSON.stringify(updatedData));
      return updatedData;
    });
  };

  return (
    <MobileContext.Provider
      value={{ mobileData: mobileData as MobileData, setMobileData, updateMobileData }}
    >
      {children}
    </MobileContext.Provider>
  );
};

export const useMobile = () => {
  const context = useContext(MobileContext);
  if (context === undefined) {
    throw new Error('useMobile must be used within a MobileProvider');
  }
  return context;
};
